import React, { useState } from "react";
import {
  View,
  Text,
  TouchableOpacity,
  Image,
  StyleProp,
  ViewStyle,
} from "react-native";
import { DEVICE_SIZES, useDeviceSize } from "rn-responsive-styles";
import { AppFontStyle } from "../../styles/AppFontStyle";
import { AppColors } from "../../utility/AppColors";
import { CommonButton } from "../../common/CommonButton";
import { BeltImages } from "../../Api/configs";
import CircularProgressList from "../profile-common-components/CircularProgressList";
import PastEventsDataSkeleton from "../skeleton-components/PastEventsDataSkeleton";
import StreamPlayer from "../../common/StreamPlayer";
import { AppImages } from "../../utility/AppImages";
import Avatar from "../choose-avatar-components/Avatar";
import { LinearGradient } from "expo-linear-gradient";

interface Profile {
  profilePicture?: string;
  userID?: string;
  coins?: number;
  bronzeBelts?: number;
  silverBelts?: number;
  goldBelts?: number;
  platinumBelts?: number;
  playedGames?: any[];
  organised?: any[];
  challenged?: any[];
  gamesWon?: any[];
  gameLost?: any[];
}

interface StreamData {
  liveStreamURL?: string;
  streams?: Record<string, { liveStreamURL: string }>;
}

interface YouTubeStreamData {
  liveStreamURL?: string;
  snippet?: {
    title?: string;
    thumbnails?: any;
  };
  liveStreamId?: string;
}

interface EventData {
  organizerProfile?: Profile;
  challengerProfile?: Profile;
  streamData?: Array<{
    organizerTwitchStream?: StreamData;
    challengerTwitchStream?: StreamData;
    organizerYoutubeStream?: YouTubeStreamData;
    challengerYoutubeStream?: YouTubeStreamData;
  }>;
  winner?: string | null;
  resultSubmitted?: boolean;
  dispute?: boolean;
  event?: {
    organiserId?: string;
  };
}

interface PlayerDetailProps {
  styles: any;
  setShowVideo: (video: { platform: string; streamUrl?: string }) => void;
  showLoader: boolean;
  eventData: EventData;
  userRole: "organiser" | "challenger";
}

const PlayerDetail: React.FC<PlayerDetailProps> = ({
  styles,
  setShowVideo,
  showLoader,
  eventData,
  userRole,
}) => {
  const deviceSize = useDeviceSize();

  const isMobileView =
    deviceSize === DEVICE_SIZES.XS ||
    deviceSize === DEVICE_SIZES.SM ||
    deviceSize === DEVICE_SIZES.MD ||
    deviceSize === DEVICE_SIZES.LG;

  const profile: Profile =
    userRole === "organiser"
      ? eventData?.organizerProfile || {}
      : eventData?.challengerProfile || {};

  const medalsData = [
    {
      id: 1,
      name: "Coins",
      image: BeltImages?.Coin,
      value: profile?.coins || 0,
    },
    {
      id: 2,
      name: "Bronze",
      image: BeltImages?.Bronze,
      value: profile?.bronzeBelts || 0,
    },
    {
      id: 3,
      name: "Silver",
      image: BeltImages?.Silver,
      value: profile?.silverBelts || 0,
    },
    {
      id: 4,
      name: "Gold",
      image: BeltImages?.Gold,
      value: profile?.goldBelts || 0,
    },
    {
      id: 5,
      name: "Platinum",
      image: BeltImages?.Platinum,
      value: profile?.platinumBelts || 0,
    },
  ];

  const twitchStreams =
    userRole === "organiser"
      ? eventData?.streamData?.[0]?.organizerTwitchStream?.streams || {}
      : eventData?.streamData?.[0]?.challengerTwitchStream?.streams || {};

  const youtubeStream =
    userRole === "organiser"
      ? eventData?.streamData?.[0]?.organizerYoutubeStream
      : eventData?.streamData?.[0]?.challengerYoutubeStream;

  console.log("PlayerDetail Debug:", {
    userRole,
    streamData: eventData?.streamData?.[0],
    twitchStreams,
    youtubeStream,
    streamList: Object.values(twitchStreams),
  });

  const streamList = Object.values(twitchStreams);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [selectedPlatform, setSelectedPlatform] = useState<
    "twitch" | "youtube"
  >("twitch");

  const hasTwitchStreams = streamList.length > 0;
  const hasYoutubeStream = youtubeStream?.liveStreamURL;

  // Auto-select available platform if current platform has no streams
  React.useEffect(() => {
    if (
      selectedPlatform === "twitch" &&
      !hasTwitchStreams &&
      hasYoutubeStream
    ) {
      setSelectedPlatform("youtube");
    } else if (
      selectedPlatform === "youtube" &&
      !hasYoutubeStream &&
      hasTwitchStreams
    ) {
      setSelectedPlatform("twitch");
    }
  }, [hasTwitchStreams, hasYoutubeStream, selectedPlatform]);

  const selectedStream = streamList[selectedIndex] || null;

  const isWinnerSet = eventData?.winner !== null && !eventData?.dispute;
  const isResultSubmitted = eventData?.resultSubmitted && !eventData?.dispute;
  const organiser = eventData?.event?.organiserId;
  const winner = eventData?.winner;

  const isOrganizerWinner = !eventData?.dispute && userRole === "organiser" && winner === organiser;
  const isChallengerWinner = !eventData?.dispute && userRole === "challenger" && winner !== organiser;

  return showLoader ? (
    <PastEventsDataSkeleton styles={styles} />
  ) : (
    <View
      style={[styles.deviceflexDirection, { justifyContent: "space-between" }]}
    >
      <View
        style={{
          width:
            deviceSize === DEVICE_SIZES.EXTRA_LARGE_DEVICE ? "48%" : "100%",
          justifyContent: "center",
        }}
      >
        <View style={styles.playerContainer}>
          {/* Platform Tabs */}
          {(hasTwitchStreams || hasYoutubeStream) && (
            <View
              style={{
                flexDirection: "row",
                marginBottom: 10,
                //@ts-ignore
                background: "linear-gradient(180deg, #1F2937 0%, #111827 100%)",
                borderRadius: 8,
                height: 38,
                padding: 3,
                paddingHorizontal: 6,
                alignItems: "center",
                gap: 18,
              }}
            >
              {hasTwitchStreams && (
                <TouchableOpacity
                  onPress={() => setSelectedPlatform("twitch")}
                  style={{
                    flex: 1,
                    paddingVertical: 8,
                    paddingHorizontal: 16,
                    borderRadius: 8,
                    backgroundColor: "transparent",
                    borderWidth: selectedPlatform === "twitch" ? 0.8 : 0,
                    borderColor:
                      selectedPlatform === "twitch" ? "#DC2626" : "transparent",
                  }}
                >
                  <Text
                    style={[
                      AppFontStyle.BOLD_ANYBODY_12,
                      {
                        color: AppColors.white,
                        textAlign: "center",
                      },
                    ]}
                  >
                    Twitch
                  </Text>
                </TouchableOpacity>
              )}
              {hasYoutubeStream && (
                <TouchableOpacity
                  onPress={() => setSelectedPlatform("youtube")}
                  style={{
                    flex: 1,
                    paddingVertical: 8,
                    paddingHorizontal: 16,
                    borderRadius: 8,
                    backgroundColor: "transparent",
                    borderWidth: selectedPlatform === "youtube" ? 0.8 : 0,
                    borderColor:
                      selectedPlatform === "youtube"
                        ? "#DC2626"
                        : "transparent",
                  }}
                >
                  <Text
                    style={[
                      AppFontStyle.BOLD_ANYBODY_12,
                      {
                        color: AppColors.white,
                        textAlign: "center",
                      },
                    ]}
                  >
                    YouTube
                  </Text>
                </TouchableOpacity>
              )}
            </View>
          )}

          {/* Stream Content */}
          {selectedPlatform === "twitch" && streamList.length > 0 ? (
            <>
              <StreamPlayer
                platform="twitch"
                streamUrl={selectedStream?.liveStreamURL}
                height={284}
                width={"100%"}
              />
              {deviceSize === DEVICE_SIZES.EXTRA_LARGE_DEVICE && (
                <TouchableOpacity
                  onPress={() =>
                    setShowVideo({
                      platform: "twitch",
                      streamUrl: selectedStream?.liveStreamURL,
                    })
                  }
                  style={{ marginTop: 10, position: "absolute", right: 10 }}
                >
                  <Image
                    source={AppImages.icExpand}
                    style={{ height: 20, width: 20 }}
                  />
                </TouchableOpacity>
              )}

              {streamList.length > 1 && (
                <View
                  style={{
                    flexDirection: "row",
                    justifyContent: "center",
                    marginTop: 10,
                    flexWrap: "wrap",
                  }}
                >
                  {streamList.map((_, idx) => (
                    <TouchableOpacity
                      key={idx}
                      onPress={() => setSelectedIndex(idx)}
                      style={{
                        paddingVertical: 6,
                        paddingHorizontal: 12,
                        borderRadius: 6,
                        margin: 4,
                        backgroundColor:
                          idx === selectedIndex
                            ? AppColors.purple_B155FF
                            : AppColors.grayColor_282828,
                      }}
                    >
                      <Text
                        style={[
                          AppFontStyle.BOLD_ANYBODY_12,
                          { color: AppColors.white },
                        ]}
                      >
                        Video {idx + 1}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
              )}
            </>
          ) : selectedPlatform === "youtube" && hasYoutubeStream ? (
            <>
              <StreamPlayer
                platform="youtube"
                streamUrl={youtubeStream?.liveStreamURL}
                height={284}
                width={"100%"}
              />
              {deviceSize === DEVICE_SIZES.EXTRA_LARGE_DEVICE && (
                <TouchableOpacity
                  onPress={() =>
                    setShowVideo({
                      platform: "youtube",
                      streamUrl: youtubeStream?.liveStreamURL,
                    })
                  }
                  style={{ marginTop: 10, position: "absolute", right: 10 }}
                >
                  <Image
                    source={AppImages.icExpand}
                    style={{ height: 20, width: 20 }}
                  />
                </TouchableOpacity>
              )}
            </>
          ) : (
            <View
              style={{
                height: 284,
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: AppColors.grayColor_282828,
                borderRadius: 8,
              }}
            >
              <Text
                style={[
                  AppFontStyle.BOLD_ANYBODY_14,
                  { color: AppColors.white },
                ]}
              >
                No Stream Found
              </Text>
            </View>
          )}
        </View>
      </View>
      {/* Player Stats Card */}
      <LinearGradient
        colors={["rgba(31, 41, 55, 0.5)", "rgba(17, 24, 39, 0.5)"]}
        start={[0, 0]}
        end={[1, 1]}
        style={{
          width:
            deviceSize === DEVICE_SIZES.EXTRA_LARGE_DEVICE ? "47%" : "100%",
          borderRadius: 8,
          borderWidth: 0.8,
          borderColor:
            isOrganizerWinner || isChallengerWinner ? "#00A63E" : "#4A5565",
          padding: isMobileView ? 8 : 16,
          marginTop: 0,
          marginBottom: isMobileView ? 12 : 0,
        }}
      >
        {/* Top Section: Profile (left) + Stats (right) - ALWAYS IN ROW */}
        <View
          style={{
            flexDirection: "row",
            marginBottom: isMobileView ? 0 : 24,
            gap: 16,
          }}
        >
          {/* Profile Section - LEFT SIDE */}
          <View style={{ alignItems: "center", marginTop: 12 }}>
            <View style={{ position: "relative" }}>
              <View
                style={{
                  backgroundColor: AppColors.lightPurple_604e74,
                  borderRadius: 40,
                  overflow: "hidden",
                }}
              >
                <Avatar src={profile?.profilePicture} variant="profile" />
              </View>
              {/* Crown Icon for Winner */}
              {(isOrganizerWinner || isChallengerWinner) && (
                <Image
                  source={AppImages.icVictoryCrown}
                  style={{
                    width: 28,
                    height: 28,
                    position: "absolute",
                    top: -10,
                    right: -8,
                  }}
                  resizeMode="contain"
                />
              )}
            </View>
            <Text
              style={{
                color: "#FFF",
                fontSize: 12,
                fontWeight: "700",
                lineHeight: 13.86,
                marginTop: 6,
              }}
            >
              {profile?.userID || "Player"}
            </Text>
          </View>

          {/* Stats - RIGHT SIDE - Simplified for mobile, Grid for desktop */}
          {isMobileView ? (
            // Mobile: Simple horizontal stats (3 items in a row)
            <View
              style={{
                flex: 1,
                flexDirection: "row",
                justifyContent: "space-around",
                alignItems: "center",
              }}
            >
              {/* Played Games */}
              <View style={{ alignItems: "center" }}>
                <Text
                  style={{
                    color: "#99A1AF",
                    fontSize: 12,
                    fontWeight: "400",
                    lineHeight: 16,
                  }}
                >
                  Played Games
                </Text>
                <Text
                  style={{
                    color: "#FFF",
                    fontSize: 16,
                    fontWeight: "400",
                    lineHeight: 18,
                    marginTop: 4,
                  }}
                >
                  {profile?.playedGames?.length || 0}
                </Text>
              </View>

              {/* Games Won */}
              <View style={{ alignItems: "center" }}>
                <Text
                  style={{
                    color: "#99A1AF",
                    fontSize: 12,
                    fontWeight: "400",
                    lineHeight: 16,
                  }}
                >
                  Games Won
                </Text>
                <Text
                  style={{
                    color: "#FFF",
                    fontSize: 16,
                    fontWeight: "400",
                    lineHeight: 18,
                    marginTop: 4,
                  }}
                >
                  {profile?.gamesWon?.length || 0}
                </Text>
              </View>

              {/* Games Lost */}
              <View style={{ alignItems: "center" }}>
                <Text
                  style={{
                    color: "#99A1AF",
                    fontSize: 12,
                    fontWeight: "400",
                    lineHeight: 16,
                  }}
                >
                  Games Lost
                </Text>
                <Text
                  style={{
                    color: "#FFF",
                    fontSize: 16,
                    fontWeight: "400",
                    lineHeight: 18,
                    marginTop: 4,
                  }}
                >
                  {profile?.gameLost?.length || 0}
                </Text>
              </View>
            </View>
          ) : (
            // Desktop: Stats Grid - 2x2 layout
            <View
              style={{
                flex: 1,
                flexDirection: "row",
                flexWrap: "wrap",
                gap: 12,
              }}
            >
              {/* Played Games */}
              <View
                style={{
                  width: 144,
                  height: 68,
                  paddingLeft: 12,
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 12,
                  borderRadius: 10,
                  backgroundColor: "rgba(16, 24, 40, 0.5)",
                }}
              >
                <View
                  style={{
                    width: 32,
                    height: 32,
                    justifyContent: "center",
                    alignItems: "center",
                    flexShrink: 0,
                    borderRadius: 32,
                    backgroundColor: "#1E5631",
                  }}
                >
                  <Image
                    source={AppImages.icPlayedGames}
                    style={{ width: 20, height: 20 }}
                    resizeMode="contain"
                  />
                </View>
                <View>
                  <Text
                    style={{
                      color: "#FFF",
                      fontFamily: "Arial",
                      fontSize: 18,
                      fontWeight: "400",
                      lineHeight: 28,
                    }}
                  >
                    {profile?.playedGames?.length || 0}
                  </Text>
                  <Text
                    style={{
                      color: "#99A1AF",
                      fontFamily: "Arial",
                      fontSize: 12,
                      fontWeight: "400",
                      lineHeight: 16,
                    }}
                  >
                    Played Games
                  </Text>
                </View>
              </View>

              {/* Games Lost */}
              <View
                style={{
                  width: 144,
                  height: 68,
                  paddingLeft: 12,
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 12,
                  borderRadius: 10,
                  backgroundColor: "rgba(16, 24, 40, 0.5)",
                }}
              >
                <View
                  style={{
                    width: 32,
                    height: 32,
                    justifyContent: "center",
                    alignItems: "center",
                    flexShrink: 0,
                    borderRadius: 32,
                    backgroundColor: "#7F1D1D",
                  }}
                >
                  <Image
                    source={AppImages.icGamesLost}
                    style={{ width: 20, height: 20 }}
                    resizeMode="contain"
                  />
                </View>
                <View>
                  <Text
                    style={{
                      color: "#FFF",
                      fontFamily: "Arial",
                      fontSize: 18,
                      fontWeight: "400",
                      lineHeight: 28,
                    }}
                  >
                    {profile?.gameLost?.length || 0}
                  </Text>
                  <Text
                    style={{
                      color: "#99A1AF",
                      fontFamily: "Arial",
                      fontSize: 12,
                      fontWeight: "400",
                      lineHeight: 16,
                    }}
                  >
                    Games Lost
                  </Text>
                </View>
              </View>

              {/* Game Won */}
              <View
                style={{
                  width: 144,
                  height: 68,
                  paddingLeft: 12,
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 12,
                  borderRadius: 10,
                  backgroundColor: "rgba(16, 24, 40, 0.5)",
                }}
              >
                <View
                  style={{
                    width: 32,
                    height: 32,
                    justifyContent: "center",
                    alignItems: "center",
                    flexShrink: 0,
                    borderRadius: 32,
                    backgroundColor: "#1E3A5F",
                  }}
                >
                  <Image
                    source={AppImages.icGamesWon}
                    style={{ width: 20, height: 20 }}
                    resizeMode="contain"
                  />
                </View>
                <View>
                  <Text
                    style={{
                      color: "#FFF",
                      fontFamily: "Arial",
                      fontSize: 18,
                      fontWeight: "400",
                      lineHeight: 28,
                    }}
                  >
                    {profile?.gamesWon?.length || 0}
                  </Text>
                  <Text
                    style={{
                      color: "#99A1AF",
                      fontFamily: "Arial",
                      fontSize: 12,
                      fontWeight: "400",
                      lineHeight: 16,
                    }}
                  >
                    Game won
                  </Text>
                </View>
              </View>

              {/* Tokens Earned */}
              <View
                style={{
                  width: 144,
                  height: 68,
                  paddingLeft: 12,
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 12,
                  borderRadius: 10,
                  backgroundColor: "rgba(16, 24, 40, 0.5)",
                }}
              >
                <View
                  style={{
                    width: 32,
                    height: 32,
                    justifyContent: "center",
                    alignItems: "center",
                    flexShrink: 0,
                    borderRadius: 32,
                    backgroundColor: "#78350F",
                  }}
                >
                  <Image
                    source={AppImages.icTokenEarned}
                    style={{ width: 20, height: 20 }}
                    resizeMode="contain"
                  />
                </View>
                <View>
                  <Text
                    style={{
                      color: "#FFF",
                      fontFamily: "Arial",
                      fontSize: 18,
                      fontWeight: "400",
                      lineHeight: 28,
                    }}
                  >
                    {profile?.coins || 0}
                  </Text>
                  <Text
                    style={{
                      color: "#99A1AF",
                      fontFamily: "Arial",
                      fontSize: 12,
                      fontWeight: "400",
                      lineHeight: 16,
                    }}
                  >
                    Tokens Earned
                  </Text>
                </View>
              </View>
            </View>
          )}
        </View>

        {/* Current Stash */}
        <View>
          <Text
            style={
              isMobileView
                ? {
                  color: "#FFF",
                  fontSize: 14,
                  fontWeight: "700",
                  lineHeight: 16.17,
                  textAlign: "center",
                  marginBottom: 16,
                }
                : [
                  AppFontStyle.BOLD_ANYBODY_20,
                  {
                    color: AppColors.white,
                    textAlign: "center",
                    marginBottom: 16,
                  },
                ]
            }
          >
            Current Stash
          </Text>
          <View
            style={{
              flexDirection: "row",
              justifyContent: "space-around",
              gap: 12,
            }}
          >
            {medalsData.map((medal) => (
              <View key={medal.id} style={{ alignItems: "center" }}>
                <Image
                  source={medal.image as any}
                  style={{
                    width: 34,
                    height: 24,
                    resizeMode: "contain",
                  }}
                />
                <Text
                  style={{
                    color: "#FFF",
                    textAlign: "center",
                    fontSize: 16,
                    fontWeight: "700",
                    lineHeight: 18,
                    marginTop: 4,
                  }}
                >
                  {medal.value}
                </Text>
                <Text
                  style={{
                    color: "#FFF",
                    textAlign: "center",
                    fontSize: 12,
                    fontWeight: "400",
                    lineHeight: 13.86,
                  }}
                >
                  {medal.name}
                </Text>
              </View>
            ))}
          </View>
        </View>
      </LinearGradient>
    </View>
  );
};

export default PlayerDetail;
